import { useState, useRef, useMemo, useCallback, useEffect } from "react";
import { useMedia } from "./MediaContext";
import { useZoom } from "./ZoomContext";
import { ZoomProvider } from "./ZoomProvider";
import {
    FilePreviewContext,
    FilePreviewContextType,
    getPredictionsLoopStartEnd,
    VideoControls
} from "./FilePreviewContext";
import { useCurrentMedia } from "@/store";
import { FileInputDimensions, MediaItemMetadata } from "@/types/media";
import { useMediaUtils } from "@/hooks/useMediaUtils";
import { isEqual } from "lodash";

export const AggregatedFilePreviewProvider: React.FC<{
    onFileChange?: () => void;
    children: React.ReactNode;
}> = ({ onFileChange, children }) => {
    const currentMedia = useCurrentMedia();
    const mediaRef = useRef<HTMLImageElement | HTMLVideoElement>(null);
    const initializedVideosRef = useRef<string[]>([]); // videos that have been viewed at least once & initial frame was extracted

    const { mediaItems } = useMedia();

    const [mediaElementReady, setMediaElementReady] = useState(false);
    const { zoomLevel, x: zoomX, y: zoomY } = useZoom();
    const { updateMediaItem } = useMediaUtils();
    const [videoControls, setVideoControls] = useState<VideoControls | null>(null);
    const [predictionsLoopTime, setPredictionsLoopTime] = useState<number | null>(null);
    const [predictionsLoopProgress, setPredictionsLoopProgress] = useState<number | undefined>();

    const videoMediaItem = useMemo(() => {
        return videoControls?.mediaItem;
    }, [videoControls]);

    const videoDuration = useMemo(
        () => videoMediaItem?.metadata?.videoMetadata?.duration || 0,
        [videoMediaItem]
    );

    const keyframeOffset = useMemo(
        () => videoMediaItem?.metadata?.keyframe_offset || 0,
        [videoMediaItem]
    );

    const adjustedPredictionsLoopTime = useMemo(() => {
        if (predictionsLoopTime == null) return null;
        return Math.max(0, predictionsLoopTime - keyframeOffset);
    }, [predictionsLoopTime, keyframeOffset]);

    // Compute clamped overlay window (1s around selected time), constrained within [0, fileDuration]
    const loopOverlayWindow = useMemo(() => {
        if (adjustedPredictionsLoopTime == null || !videoDuration || videoDuration <= 0)
            return null;

        const { loopStart, loopEnd } = getPredictionsLoopStartEnd(
            adjustedPredictionsLoopTime,
            videoDuration
        );
        const leftPercent = (loopStart / videoDuration) * 100;
        const widthPercent = ((loopEnd - loopStart) / videoDuration) * 100;
        return { leftPercent, widthPercent, loopStart, loopEnd };
    }, [adjustedPredictionsLoopTime, videoDuration]);

    const onVideoPause = useCallback(
        (_currentTime: number) => {
            //         if (step === BuildSteps.REVIEW) {
            //             if (!predictionsLoopTime) {
            //                 setPredictionsLoopTime(currentTime);
            //             }
            //         }
        },
        [
            // step,
            // predictionsLoopTime
        ]
    );

    const stableSetters = useMemo<
        Pick<
            FilePreviewContextType,
            "setVideoControls" | "setPredictionsLoopTime" | "setPredictionsLoopProgress"
        >
    >(() => ({ setVideoControls, setPredictionsLoopTime, setPredictionsLoopProgress }), []);

    const handleMediaReady = useCallback(() => {
        if (mediaRef.current) {
            setMediaElementReady(true);
        }
    }, []);

    const handleGetFileDimensions = useCallback(
        (dimensions: { natural: FileInputDimensions; rendered: FileInputDimensions }) => {
            if (!currentMedia?.file || !dimensions) return;

            // don't update if the dimensions are the same
            if (
                isEqual(currentMedia.file.metadata?.renderedDimensions, dimensions.rendered) &&
                isEqual(currentMedia.file.metadata?.naturalDimensions, dimensions.natural)
            )
                return;

            const newMetadata = {
                ...currentMedia.file.metadata,
                renderedDimensions: dimensions.rendered,
                naturalDimensions: dimensions.natural
            } as MediaItemMetadata;

            // Only update the metadata field, don't spread the entire file object
            // as it may contain stale status values that would overwrite the correct status
            updateMediaItem({ id: currentMedia.file.id, metadata: newMetadata } as MediaItem);
        },
        [currentMedia?.file, updateMediaItem]
    );

    useEffect(() => {
        // Don't reset dimensions here - let FilePreview handle them through onGetFileDimensions
        setMediaElementReady(false);
        onFileChange?.();
    }, [currentMedia?.file.id, onFileChange]);

    const contextValue = useMemo<FilePreviewContextType>(
        () => ({
            mediaRef,
            initializedVideosRef,
            mediaElementReady,
            handleGetFileDimensions,
            handleMediaReady,
            zoomLevel,
            zoomX,
            zoomY,
            hasMultipleFiles: mediaItems.length > 1,
            videoControls,
            onVideoPause,
            predictionsLoopTime,
            predictionsLoopProgress,
            loopOverlayWindow,
            ...stableSetters
        }),
        [
            mediaItems.length,
            mediaRef,
            initializedVideosRef,
            mediaElementReady,
            handleGetFileDimensions,
            handleMediaReady,
            zoomLevel,
            zoomX,
            zoomY,
            videoControls,
            onVideoPause,
            predictionsLoopTime,
            predictionsLoopProgress,
            loopOverlayWindow,
            stableSetters
        ]
    );

    return (
        <FilePreviewContext.Provider value={contextValue}>{children}</FilePreviewContext.Provider>
    );
};

export const FilePreviewProvider: React.FC<{
    onFileChange?: () => void;
    children: React.ReactNode;
}> = ({ onFileChange, children }) => {
    return (
        <ZoomProvider>
            <AggregatedFilePreviewProvider onFileChange={onFileChange}>
                {children}
            </AggregatedFilePreviewProvider>
        </ZoomProvider>
    );
};
